import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import type {
  File,
  LocalFile,
  SyncableLocalFile,
  SyncedLocalFile,
} from '@actual-app/core/types/file';

import { useAuth } from '#auth/AuthProvider';
import { Permissions } from '#auth/types';
import {
  closeAndDownloadBudget,
  closeAndLoadBudget,
  downloadBudget,
  loadAllFiles,
  loadBudget,
} from '#budgetfiles/budgetfilesSlice';
import { abrirTripulacion } from '#components/pareja/abrirTripulacion';
import { useMultiuserEnabled } from '#components/ServerContext';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { pushModal } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch, useSelector } from '#redux';
import { getUserData } from '#users/usersSlice';

import { Puerto } from './puerto/Puerto';
import { useBotarBarco } from './puerto/useBotarBarco';

// Rumbo: la lista de presupuestos es el Puerto (manager/puerto).
export function BudgetFileSelection() {
  const dispatch = useDispatch();
  const allFiles = useSelector(state => state.budgetfiles.allFiles || []);
  const multiuserEnabled = useMultiuserEnabled();
  const [id] = useMetadataPref('id');
  const [currentUserId, setCurrentUserId] = useState('');
  const userData = useSelector(state => state.user.data);
  const { hasPermission } = useAuth();
  const { t } = useTranslation();

  const fetchUsers = useCallback(async () => {
    try {
      setCurrentUserId(userData?.userId ?? '');
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [userData?.userId]);

  useEffect(() => {
    if (multiuserEnabled && !userData?.offline) {
      void fetchUsers();
    }
  }, [multiuserEnabled, userData?.offline, fetchUsers]);

  // Remote files do not have the 'id' field
  function isNonRemoteFile(
    file: File,
  ): file is LocalFile | SyncableLocalFile | SyncedLocalFile {
    return file.state !== 'remote';
  }

  // Filter out the open file
  const files = id
    ? allFiles.filter(file => !isNonRemoteFile(file) || file.id !== id)
    : allFiles;

  const { onBotar } = useBotarBarco();

  const refresh = () => {
    void dispatch(getUserData());
    void dispatch(loadAllFiles());
  };

  const onSelect = async (file: File): Promise<void> => {
    const isRemoteFile = file.state === 'remote';

    if (!id) {
      if (isRemoteFile) {
        await dispatch(downloadBudget({ cloudFileId: file.cloudFileId }));
      } else {
        await dispatch(loadBudget({ id: file.id }));
      }
    } else if (!isRemoteFile && file.id !== id) {
      await dispatch(closeAndLoadBudget({ fileId: file.id }));
    } else if (isRemoteFile) {
      await dispatch(closeAndDownloadBudget({ cloudFileId: file.cloudFileId }));
    }
  };

  return (
    <Puerto
      files={files}
      currentUserId={userData?.userId ?? currentUserId}
      esAdmin={hasPermission(Permissions.ADMINISTRATOR)}
      onSelect={onSelect}
      onBorrar={file =>
        dispatch(
          pushModal({ modal: { name: 'delete-budget', options: { file } } }),
        )
      }
      onCompartir={file => {
        if ('cloudFileId' in file && file.cloudFileId) {
          dispatch(abrirTripulacion(file.cloudFileId, file.name));
        }
      }}
      onRenombrar={async (file, nombre) => {
        const { error } = await send('rumbo-renombrar-presupuesto', {
          id: 'id' in file ? file.id : undefined,
          cloudFileId: 'cloudFileId' in file ? file.cloudFileId : undefined,
          nombre,
        });
        if (error) {
          dispatch(
            addNotification({
              notification: {
                type: 'error',
                message: t('Could not rename the budget. Try again.'),
              },
            }),
          );
        }
        refresh();
      }}
      onPasarAMiCuenta={async file => {
        if (!('cloudFileId' in file)) {
          return t('Could not move the budget to your account.');
        }
        // Se toma del usuario conectado, no de currentUserId, que se queda
        // vacío si el servidor no avisa de multiusuario y antes hacía que
        // el botón no hiciera nada sin decirlo.
        const yo = userData?.userId;
        const respuesta = yo
          ? await send('transfer-ownership', {
              fileId: file.cloudFileId,
              newUserId: yo,
            })
          : { error: 'sin-usuario' };
        if (respuesta && 'error' in respuesta) {
          // El motivo (en inglés, del servidor) ayuda a saber qué falló.
          return `${t('Could not move the budget to your account.')} (${respuesta.error || 'error'})`;
        }
        dispatch(
          addNotification({
            notification: {
              type: 'message',
              message: t('{{name}} is now in your account.', {
                name: file.name,
              }),
            },
          }),
        );
        refresh();
        return null;
      }}
      onBotar={onBotar}
      onImportar={() => dispatch(pushModal({ modal: { name: 'import' } }))}
    />
  );
}
